/**
 * ResearchConfigStep
 *
 * Step 7: Configure research settings for B2B/B2C and category normalization.
 * Part of AEO Onboarding Wizard
 *
 * Date: February 12, 2026
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  Building2,
  Users,
  Layers,
  GitBranch,
  Loader2,
  HelpCircle
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ResearchConfigStepProps {
  detectedSector?: { name: string; id: number } | null;
  onSubmit: (config: { businessModel: 'b2b' | 'b2c'; categoryNormalization: boolean }) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function ResearchConfigStep({
  detectedSector,
  onSubmit,
  isLoading,
  error
}: ResearchConfigStepProps) {
  const [businessModel, setBusinessModel] = useState<'b2b' | 'b2c'>('b2c');
  const [categoryNormalization, setCategoryNormalization] = useState(true);

  const handleSubmit = async () => {
    if (isLoading) return;
    await onSubmit({ businessModel, categoryNormalization });
  };

  // Auto-suggest based on detected sector name
  const sectorName = detectedSector?.name?.toLowerCase() || '';
  const isLikelyB2B = sectorName.includes('industrial') ||
    sectorName.includes('manufacturing') ||
    sectorName.includes('equipment') ||
    sectorName.includes('machinery') ||
    sectorName.includes('wholesale') ||
    sectorName.includes('b2b');

  // Update defaults based on sector detection
  React.useEffect(() => {
    if (isLikelyB2B) {
      setBusinessModel('b2b');
      setCategoryNormalization(false);
    }
  }, [isLikelyB2B]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configure Research Settings</CardTitle>
        <CardDescription>
          These settings affect how AI researches buyer personas and product categories.
          {detectedSector && (
            <span className="block mt-2">
              Detected sector: <Badge variant="outline">{detectedSector.name}</Badge>
              {isLikelyB2B && (
                <span className="text-blue-600 ml-2">(B2B settings recommended)</span>
              )}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Business Model Selection */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">Business Model</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p>This affects the type of buyer personas generated. B2B creates business roles (Procurement Managers, Engineers), while B2C creates consumer personas (Hobbyists, Enthusiasts).</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-sm text-muted-foreground">
            Who are your primary customers?
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* B2B Option */}
            <Card
              className={`cursor-pointer transition-all hover:border-primary ${
                businessModel === 'b2b' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
              } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => setBusinessModel('b2b')}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Building2 className="h-8 w-8 text-blue-600" />
                  {isLikelyB2B && <Badge variant="secondary">Recommended</Badge>}
                </div>
                <CardTitle className="text-base mt-3">B2B (Business-to-Business)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>You sell to other businesses, not directly to consumers.</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Procurement Managers</li>
                  <li>Engineers & Technical Buyers</li>
                  <li>Fleet/Operations Directors</li>
                  <li>OEM Product Developers</li>
                </ul>
              </CardContent>
            </Card>

            {/* B2C Option */}
            <Card
              className={`cursor-pointer transition-all hover:border-primary ${
                businessModel === 'b2c' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
              } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => setBusinessModel('b2c')}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Users className="h-8 w-8 text-green-600" />
                  {!isLikelyB2B && <Badge variant="secondary">Default</Badge>}
                </div>
                <CardTitle className="text-base mt-3">B2C (Business-to-Consumer)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>You sell directly to individual consumers.</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Hobbyists & Enthusiasts</li>
                  <li>Casual Users</li>
                  <li>Price-Conscious Shoppers</li>
                  <li>Premium Buyers</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Category Normalization Selection */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">Category Research Mode</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p>Normalized mode groups similar categories together (e.g., "Running Shoes" → "Athletic Footwear"). Specific mode researches each category exactly as named, preserving your product hierarchy.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-sm text-muted-foreground">
            How should AI research your product categories?
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Specific (No Normalization) */}
            <Card
              className={`cursor-pointer transition-all hover:border-primary ${
                !categoryNormalization ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
              } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => setCategoryNormalization(false)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <GitBranch className="h-8 w-8 text-purple-600" />
                  {businessModel === 'b2b' && <Badge variant="secondary">Recommended for B2B</Badge>}
                </div>
                <CardTitle className="text-base mt-3">Specific Categories</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>Research each category exactly as defined in your feed.</p>
                <div className="text-xs bg-muted p-2 rounded font-mono">
                  "Wiper Systems {'>'} Agriculture {'>'} Direct Drive"<br/>
                  → researched as-is
                </div>
                <p className="text-xs">Best for: Industrial products, B2B, technical hierarchies</p>
              </CardContent>
            </Card>

            {/* Normalized */}
            <Card
              className={`cursor-pointer transition-all hover:border-primary ${
                categoryNormalization ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
              } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => setCategoryNormalization(true)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Layers className="h-8 w-8 text-orange-600" />
                  {businessModel === 'b2c' && <Badge variant="secondary">Recommended for B2C</Badge>}
                </div>
                <CardTitle className="text-base mt-3">Normalized Categories</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>Group similar categories together for shared research.</p>
                <div className="text-xs bg-muted p-2 rounded font-mono">
                  "Running Shoes {'>'} Trail {'>'} Waterproof"<br/>
                  → normalized to "Athletic Footwear"
                </div>
                <p className="text-xs">Best for: Consumer products, retail, standard categories</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4 border-t">
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            size="lg"
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Saving Configuration...
              </>
            ) : (
              'Continue with Research'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
